import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import EyeIcon from "../../../assets/svg/eye.svg";
import ClosedEyeIcon from "../../../assets/svg/eyeOff.svg";
import { getString } from "../../utilities";

export default function TextViewHook({
  name,
  type = "text",
  shouldPasswordToggle = false,
  defaultValue,
  errors,
  placeholder,
  label,
  className = "",
  isReadOnly = false,
  isDisabled = false,
  children = null,
  onChange,
  rules,
  dataTestId,
  autoComplete,
  shouldLabelAnimate = true,
}) {
  const [showLabel, setShowLabel] = useState(false);
  const [tagType, setTagType] = useState(type);
  const { register, getValues } = useFormContext();

  const validationErrorId = `${name}-validation-error`;
  const passwordType = "password";
  const isPasswordType = type === passwordType;
  let shouldAutoComplete = autoComplete;

  if (!shouldAutoComplete && isPasswordType) {
    shouldAutoComplete = "off";
  }

  const formFieldClass = `form-field ${className}`;

  useEffect(() => {
    const setShowLabelFunction = () => {
      const inputValue = getValues(name);

      if (inputValue && inputValue.length > 0 && shouldLabelAnimate) {
        setShowLabel(true);
      }
    };

    const labelTimer = setTimeout(setShowLabelFunction, 100);

    return () => {
      clearTimeout(labelTimer);
    };
  }, []);

  const handleInputChange = (e) => {
    if (onChange) {
      onChange(e);
    }

    setShowLabel(e.target.value.length > 0 && shouldLabelAnimate);
  };

  const renderLabel = () => {
    if (label && label.length > 0) {
      const labelClass = showLabel && shouldLabelAnimate ? "active" : "";

      return (
        <label htmlFor={name} className={labelClass}>
          {label}
        </label>
      );
    }

    return null;
  };

  const renderTag = () => {
    const hold = placeholder || label || "";
    const autoCompleteProp = shouldAutoComplete ? { autoComplete: shouldAutoComplete } : {};
    const describedByProp = errors[name] ? { "aria-describedby": validationErrorId } : {};

    if (type === "textarea") {
      return (
        <textarea
          id={name}
          placeholder={hold}
          defaultValue={defaultValue || ""}
          {...register(name, { ...rules, onChange: handleInputChange })}
          readOnly={isReadOnly}
          disabled={isDisabled}
          data-testid={dataTestId}
          aria-invalid={!!errors[name]}
          {...describedByProp}
        />
      );
    }

    return (
      <input
        id={name}
        type={tagType}
        placeholder={hold}
        defaultValue={defaultValue || ""}
        {...register(name, { ...rules, onChange: handleInputChange })}
        readOnly={isReadOnly}
        disabled={isDisabled}
        data-testid={dataTestId}
        aria-invalid={!!errors[name]}
        {...autoCompleteProp}
        {...describedByProp}
      />
    );
  };

  const renderPasswordToggle = () => {
    if (isPasswordType && shouldPasswordToggle) {
      const isTagPassword = tagType === passwordType;
      const renderEyeIcon = () => {
        if (isTagPassword) {
          return <EyeIcon />;
        }

        return <ClosedEyeIcon />;
      };

      return (
        <a
          role="button"
          title={getString("password.toggle")}
          className="form-field-password-reveal"
          onClick={() => {
            if (isTagPassword) {
              setTagType("text");
            } else {
              setTagType(passwordType);
            }

            return false;
          }}
        >
          {renderEyeIcon()}
        </a>
      );
    }

    return null;
  };

  const containerStylesArray = ["form-field-container"];
  if (isPasswordType && shouldPasswordToggle) {
    containerStylesArray.push("password");
  }
  if (errors[name]) {
    containerStylesArray.push("form-field-error");
  }

  return (
    <div className={formFieldClass}>
      {children}
      <div className={containerStylesArray.join(" ")}>
        {renderLabel()}
        {renderTag()}
        {renderPasswordToggle()}
        {errors[name] && (
          <div className="validation-error" id={validationErrorId} role="alert" aria-live="polite">
            {errors[name].message}
          </div>
        )}
      </div>
    </div>
  );
}
